import { AnimatePresence, motion, useInView } from "framer-motion";
import {
  ArrowRight,
  Award,
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  DollarSign,
  GraduationCap,
  MapPin,
  Play,
  Search,
  Shield,
  Star,
  Users,
  X,
  Zap,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../contexts/languageStore";
import { useWebsite } from "../contexts/websiteStore";

// Simple Card components since we don't have a UI library
const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`bg-white rounded-xl shadow-lg ${className}`}>{children}</div>
);

const CardContent = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={className}>{children}</div>;

interface JobOpening {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  salary: string;
  experience: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
  tags: string[];
  featured?: boolean;
}

export function CareersPage() {
  const { t } = useLanguage();
  const { config, getAsset } = useWebsite();
  const leadershipTeam = [
    { key: 'financial', icon: Users, delay: 0.1 },
    { key: 'ceo', icon: Users, delay: 0.2 },
    { key: 'operations', icon: Users, delay: 0.3 }
  ].filter(member => {
    // Check if translation key exists
    const translationKey = `careers.leadership.${member.key}.name`;
    return t(translationKey) !== translationKey; // If key doesn't exist, i18n returns the key itself
  });

  const getGridCols = (count: number) => {
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-3';
  };
  const [selectedJob, setSelectedJob]
   = useState<JobOpening | null>(null);
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jobOpenings: JobOpening[] = useMemo(
    () => [
      {
        id: "1",
        title: t("careers.jobs.job1.title"),
        department: t("careers.jobs.job1.dept"),
        location: t("careers.jobs.job1.location"),
        type: t("careers.jobs.job1.type"),
        salary: t("careers.jobs.job1.salary"),
        experience: t("careers.jobs.job1.exp"),
        description: t("careers.jobs.job1.desc"),
        requirements: [
          t("careers.jobs.job1.req1"),
          t("careers.jobs.job1.req2"),
          t("careers.jobs.job1.req3"),
          t("careers.jobs.job1.req4"),
          t("careers.jobs.job1.req5"),
        ],
        responsibilities: [
          t("careers.jobs.job1.resp1"),
          t("careers.jobs.job1.resp2"),
          t("careers.jobs.job1.resp3"),
          t("careers.jobs.job1.resp4"),
          t("careers.jobs.job1.resp5"),
        ],
        tags: [
          t("careers.jobs.job1.tag1"),
          t("careers.jobs.job1.tag2"),
          t("careers.jobs.job1.tag3"),
        ],
        featured: true,
      },
      {
        id: "2",
        title: t("careers.jobs.job2.title"),
        department: t("careers.jobs.job2.dept"),
        location: t("careers.jobs.job2.location"),
        type: t("careers.jobs.job2.type"),
        salary: t("careers.jobs.job2.salary"),
        experience: t("careers.jobs.job2.exp"),
        description: t("careers.jobs.job2.desc"),
        requirements: [
          t("careers.jobs.job2.req1"),
          t("careers.jobs.job2.req2"),
          t("careers.jobs.job2.req3"),
          t("careers.jobs.job2.req4"),
          t("careers.jobs.job2.req5"),
        ],
        responsibilities: [
          t("careers.jobs.job2.resp1"),
          t("careers.jobs.job2.resp2"),
          t("careers.jobs.job2.resp3"),
          t("careers.jobs.job2.resp4"),
          t("careers.jobs.job2.resp5"),
        ],
        tags: [
          t("careers.jobs.job2.tag1"),
          t("careers.jobs.job2.tag2"),
          t("careers.jobs.job2.tag3"),
        ],
      },
      {
        id: "3",
        title: t("careers.jobs.job3.title"),
        department: t("careers.jobs.job3.dept"),
        location: t("careers.jobs.job3.location"),
        type: t("careers.jobs.job3.type"),
        salary: t("careers.jobs.job3.salary"),
        experience: t("careers.jobs.job3.exp"),
        description: t("careers.jobs.job3.desc"),
        requirements: [
          t("careers.jobs.job3.req1"),
          t("careers.jobs.job3.req2"),
          t("careers.jobs.job3.req3"),
          t("careers.jobs.job3.req4"),
          t("careers.jobs.job3.req5"),
        ],
        responsibilities: [
          t("careers.jobs.job3.resp1"),
          t("careers.jobs.job3.resp2"),
          t("careers.jobs.job3.resp3"),
          t("careers.jobs.job3.resp4"),
          t("careers.jobs.job3.resp5"),
        ],
        tags: [
          t("careers.jobs.job3.tag1"),
          t("careers.jobs.job3.tag2"),
          t("careers.jobs.job3.tag3"),
        ],
      },
      {
        id: "4",
        title: t("careers.jobs.job4.title"),
        department: t("careers.jobs.job4.dept"),
        location: t("careers.jobs.job4.location"),
        type: t("careers.jobs.job4.type"),
        salary: t("careers.jobs.job4.salary"),
        experience: t("careers.jobs.job4.exp"),
        description: t("careers.jobs.job4.desc"),
        requirements: [
          t("careers.jobs.job4.req1"),
          t("careers.jobs.job4.req2"),
          t("careers.jobs.job4.req3"),
          t("careers.jobs.job4.req4"),
          t("careers.jobs.job4.req5"),
        ],
        responsibilities: [
          t("careers.jobs.job4.resp1"),
          t("careers.jobs.job4.resp2"),
          t("careers.jobs.job4.resp3"),
          t("careers.jobs.job4.resp4"),
          t("careers.jobs.job4.resp5"),
        ],
        tags: [
          t("careers.jobs.job4.tag1"),
          t("careers.jobs.job4.tag2"),
          t("careers.jobs.job4.tag3"),
        ],
      },
    ],
    [t],
  );

  const filteredJobs = useMemo(() => {
    return jobOpenings.filter((job) => {
      const matchesSearch =
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment =
        !departmentFilter || job.department === departmentFilter;
      const matchesLocation =
        !locationFilter || job.location === locationFilter;
      const matchesType = !typeFilter || job.type === typeFilter;
      return (
        matchesSearch && matchesDepartment && matchesLocation && matchesType
      );
    });
  }, [searchTerm, departmentFilter, locationFilter, typeFilter, jobOpenings]);

  const clearFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("");
    setLocationFilter("");
    setTypeFilter("");
  };

  const handleApply = (job: JobOpening) => {
    setSelectedJob(job);
    setIsApplicationOpen(true);
  };

  const scrollToOpenPositions = () => {
    const openPositionsSection = document.querySelector("#open-positions");
    if (openPositionsSection) {
      const offset = 80; // Offset to show the full section header
      const elementPosition = openPositionsSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const departments = [...new Set(jobOpenings.map((job) => job.department))];
  const locations = [...new Set(jobOpenings.map((job) => job.location))];
  const types = [...new Set(jobOpenings.map((job) => job.type))];

  const teams = [
    {
      title: t("careers.teams.team1.title"),
      description: t("careers.teams.team1.desc"),
      image: getAsset(config.assets.team1),
      color: "from-blue-500/20 to-purple-500/20",
    },
    {
      title: t("careers.teams.team2.title"),
      description: t("careers.teams.team2.desc"),
      image: getAsset(config.assets.team2),
      color: "from-green-500/20 to-teal-500/20",
    },
    {
      title: t("careers.teams.team3.title"),
      description: t("careers.teams.team3.desc"),
      image: getAsset(config.assets.team3),
      color: "from-orange-500/20 to-red-500/20",
    },
    {
      title: t("careers.teams.team4.title"),
      description: t("careers.teams.team4.desc"),
      image: getAsset(config.assets.team4),
      color: "from-purple-500/20 to-pink-500/20",
    },
    {
      title: t("careers.teams.team5.title"),
      description: t("careers.teams.team5.desc"),
      image: getAsset(config.assets.team5),
      color: "from-indigo-500/20 to-blue-500/20",
    },
    {
      title: t("careers.teams.team6.title"),
      description: t("careers.teams.team6.desc"),
      image: getAsset(config.assets.team6),
      color: "from-emerald-500/20 to-green-500/20",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {/* Background Image */}
          <img
            src={getAsset(config.assets.careersBg || "about-bg.webp")}
            alt="Careers Background"
            className="w-full h-full object-cover"
          />

          {/* Professional overlay with darker gradients */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center text-white max-w-4xl mx-auto"
          >
            <motion.div
              className="inline-flex items-center px-6 py-3 bg-black/30 text-white border border-white/20 text-sm font-light tracking-wider mb-8 backdrop-blur-sm rounded-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <Briefcase className="w-4 h-4 mr-2" />
              <span className="uppercase tracking-[0.2em]">
                {t("careers.hero.badge")}
              </span>
            </motion.div>

            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-6 text-white"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 1.2 }}
            >
              {t("careers.hero.title")}
            </motion.h1>

            <motion.h2
              className="text-2xl md:text-3xl font-light mb-8 text-blue-100"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 1.2 }}
            >
              {t("careers.hero.description")}
            </motion.h2>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <motion.button
                onClick={scrollToOpenPositions}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary text-white px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t("careers.hero.exploreButton")}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-white/10 transition-all duration-300"
              >
                <Play className="w-5 h-5 mr-2 inline" />
                {t("careers.hero.watchButton")}
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Work Section */}
      <section
        id="work-section"
        className="py-20 bg-white relative overflow-hidden"
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t("careers.work.foryour.company.name")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("careers.work.desc")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Job Search Section */}
      <section id="open-positions" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t("careers.openPositions.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("careers.openPositions.description")}
            </p>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("careers.filters.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
                />
              </div>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
              >
                <option value="">{t("careers.filters.department")}</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
              >
                <option value="">{t("careers.filters.location")}</option>
                {locations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
              >
                <option value="">{t("careers.filters.jobType")}</option>
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            {(searchTerm ||
              departmentFilter ||
              locationFilter ||
              typeFilter) && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={clearFilters}
                  className="mt-4 text-primary hover:text-primary/80 text-sm font-medium"
                >
                  {t("careers.filters.clearAll")}
                </motion.button>
              )}
          </motion.div>

          {/* Job Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 auto-rows-min">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group"
              >
                <div
                  className={`bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border ${job.featured
                      ? "border-2 border-primary/50 bg-gradient-to-br from-primary/5 to-blue-50"
                      : "border-gray-100"
                    }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {job.featured && (
                            <span className="px-2 py-1 bg-primary text-white text-xs font-medium rounded-full flex items-center">
                              <Star className="w-3 h-3 mr-1" />
                              {t("careers.featured")}
                            </span>
                          )}
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                            {job.department}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                          {job.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job.location}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {job.type}
                          </div>
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {job.salary}
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {job.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        {job.experience} {t("careers.experience")}
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleApply(job)}
                        className="bg-primary text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
                      >
                        {t("careers.applyNow")}
                      </motion.button>
                    </div>

                    <AnimatePresence>
                      {expandedJob === job.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t"
                        >
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-semibold mb-2">
                                {t("careers.requirements")}:
                              </h4>
                              <ul className="space-y-1 text-sm text-gray-600">
                                {job.requirements.map((req, i) => (
                                  <li key={i} className="flex items-start">
                                    <CheckCircle className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                    {req}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-2">
                                {t("careers.responsibilities")}:
                              </h4>
                              <ul className="space-y-1 text-sm text-gray-600">
                                {job.responsibilities.map((resp, i) => (
                                  <li key={i} className="flex items-start">
                                    <CheckCircle className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                    {resp}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.button
                      onClick={() =>
                        setExpandedJob(expandedJob === job.id ? null : job.id)
                      }
                      className="w-full mt-4 text-primary hover:text-primary/80 text-sm font-medium flex items-center justify-center"
                      whileHover={{ scale: 1.02 }}
                    >
                      {expandedJob === job.id ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          {t("careers.showLess")}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          {t("careers.viewDetails")}
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t("careers.leadership.title")}
            </h2>
          </motion.div>

          <div className={`grid ${getGridCols(leadershipTeam.length)} gap-8 max-w-6xl mx-auto`}>
            {leadershipTeam.map((member) => (
              <motion.div
                key={member.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: member.delay }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <member.icon className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {t(`careers.leadership.${member.key}.name`)}
                  </h3>
                 {
                  config.name !== "EMCOR" && (
                  <p className="text-lg text-primary font-semibold mb-4">
                    {t(`careers.leadership.${member.key}.title`)}
                  </p>
                  ) }
                  <p className="text-gray-600">
                      {t(`careers.leadership.${member.key}.description`)}
                    </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Team Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t("careers.findTeam.title")}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("careers.findTeam.description")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teams.map((team, index) => (
              <motion.div
                key={team.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="group cursor-pointer"
              >
                <Card className="h-full overflow-hidden hover:shadow-2xl transition-all duration-500">
                  <div className="relative overflow-hidden">
                    <img
                      src={team.image}
                      alt={team.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div
                      className={`absolute inset-0 bg-gradient-to-t ${team.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors">
                      {team.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {team.description}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full bg-primary text-white py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center"
                    >
                      {t("careers.exploreTeam")}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </motion.button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Modal */}
      <AnimatePresence>
        {isApplicationOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsApplicationOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t("careers.application.title")} {selectedJob?.title}
                </h2>
                <button
                  onClick={() => setIsApplicationOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("careers.application.firstName")}
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("careers.application.lastName")}
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("careers.application.email")}
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("careers.application.phone")}
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("careers.application.coverLetter")}
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("careers.application.resume")}
                  </label>
                  <input
                    type="file"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button className="flex-1 bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors">
                    {t("careers.application.submit")}
                  </button>
                  <button
                    onClick={() => setIsApplicationOpen(false)}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    {t("careers.application.cancel")}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
